import { useState } from "react";
import { Search, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import client from "@/lib/api";
import { ytId, vidflowUrl } from "@/lib/quoteContent";

const MAX_VIDEOS = 3;

function SelectedThumbs({ selected, onToggle }) {
  if (!selected.length) return null;
  return (
    <div className="flex flex-wrap gap-3 mb-4" data-testid="selected-venue-videos">
      {selected.map((u) => {
        const id = ytId(u);
        return (
          <div key={u} className="relative w-40 group">
            {id ? (
              <img
                src={`https://img.youtube.com/vi/${id}/mqdefault.jpg`}
                alt=""
                className="w-full aspect-video object-cover border-2 border-[#2F2E2E]"
              />
            ) : (
              <div className="w-full aspect-video border-2 border-[#2F2E2E] bg-[#21201F] text-white flex items-center justify-center text-[10px] tracking-overline uppercase px-2 text-center">
                Vidflow video
              </div>
            )}
            <button
              type="button"
              onClick={() => onToggle(u)}
              data-testid={`remove-venue-video-${id || "vidflow"}`}
              className="absolute -top-2 -right-2 bg-[#8A3D3D] text-white rounded-full p-1 shadow"
              title="Remove"
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

function ResultsGrid({ results, selected, onToggle }) {
  if (!results.length) {
    return <div className="text-sm text-[#8A8782]">No matches found.</div>;
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {results.map((v) => {
        const isSel = selected.includes(v.url);
        return (
          <button
            type="button"
            key={v.video_id}
            onClick={() => onToggle(v.url)}
            data-testid={`venue-video-result-${v.video_id}`}
            className={`text-left border-2 transition-colors ${
              isSel ? "border-[#CCBA56]" : "border-transparent hover:border-[#D1CEC2]"
            }`}
          >
            <img src={v.thumbnail} alt="" className="w-full aspect-video object-cover" />
            <div className={`text-xs p-2 leading-snug ${isSel ? "text-[#1F1E1D] font-medium" : "text-[#5C5A56]"}`}>
              {v.title}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export function VenueVideoPicker({ venueName, urls, onChange }) {
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [manual, setManual] = useState("");
  const selected = urls || [];

  async function search() {
    if (!venueName) {
      toast.error("Enter or pull the venue name first");
      return;
    }
    setSearching(true);
    try {
      const r = await client.get("/youtube/venue-search", {
        params: { venue: venueName },
      });
      setResults(r.data.results);
      if (!r.data.results.length) {
        toast.info(`No videos matching "${venueName}" found on your channel`);
      }
    } catch {
      toast.error("YouTube search failed");
    } finally {
      setSearching(false);
    }
  }

  function toggle(url) {
    if (selected.includes(url)) {
      onChange(selected.filter((u) => u !== url));
    } else if (selected.length >= MAX_VIDEOS) {
      toast.error(`Maximum ${MAX_VIDEOS} videos on a quote`);
    } else {
      onChange([...selected, url]);
    }
  }

  function addManual() {
    const id = ytId(manual);
    if (id) {
      toggle(`https://youtu.be/${id}`);
      setManual("");
      return;
    }
    const vf = vidflowUrl(manual);
    if (vf) {
      toggle(vf);
      setManual("");
      return;
    }
    toast.error("That doesn't look like a YouTube or Vidflow link");
  }

  return (
    <div data-testid="venue-video-picker">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-[10px] tracking-overline uppercase text-[#8A8782]">
          Venue Example Videos <span className="normal-case text-[#B0ADA6]">(up to {MAX_VIDEOS}, shown on the quote)</span>
        </label>
        <button
          type="button"
          onClick={search}
          disabled={searching}
          data-testid="venue-video-search-btn"
          className="inline-flex items-center gap-2 border border-[#2F2E2E] text-[#2F2E2E] hover:bg-[#2F2E2E] hover:text-white px-3 py-2 text-[10px] tracking-overline uppercase font-medium transition-colors disabled:opacity-60"
        >
          {searching ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
          ) : (
            <Search className="w-3.5 h-3.5" strokeWidth={1.5} />
          )}
          Find videos at {venueName ? `"${venueName}"` : "venue"}
        </button>
      </div>

      <SelectedThumbs selected={selected} onToggle={toggle} />

      {results && (
        <div className="border border-[#E5E3DB] bg-white p-4 mb-4 max-h-80 overflow-y-auto" data-testid="venue-video-results">
          <ResultsGrid results={results} selected={selected} onToggle={toggle} />
        </div>
      )}

      <div className="flex items-end gap-3">
        <input
          type="url"
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          data-testid="venue-video-manual-input"
          placeholder="Or paste a YouTube / Vidflow link… https://youtu.be/…"
          className="flex-1 border-b border-[#D1CEC2] bg-transparent text-base py-2 focus:border-[#2F2E2E] outline-none"
        />
        <button
          type="button"
          onClick={addManual}
          data-testid="venue-video-manual-add"
          className="text-[11px] tracking-overline uppercase text-[#5C5A56] hover:text-[#2F2E2E] font-medium pb-2"
        >
          Add
        </button>
      </div>
      <p className="text-xs text-[#8A8782] mt-2">
        Searches your YouTube channel for films at this venue. Selected videos appear in the "captured at {venueName || "your venue"}" section.
      </p>
    </div>
  );
}
